/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement } from "@lexical/utils";
import { isContractValidationPage, isSetupPage } from "helpers/utils";
import type {
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from "lexical";
import { DOMConversionMap, ElementNode } from "lexical";

import type { RepeatBy } from "./repeatBy";

type SerializedRepeatableContainerNode = Spread<
    {
        open: boolean;
        repeat: boolean;
        repeatBy?: RepeatBy;
    },
    SerializedElementNode
>;

export function convertDetailsElement(domNode: HTMLDetailsElement): DOMConversionOutput | null {
    const isOpen = domNode.open !== undefined ? domNode.open : true;
    const repeatBy = domNode.getAttribute("data-repeat-by") === "article" ? "article" : "beneficiary";
    const node = $createRepeatableContainerNode(isOpen, true, repeatBy);
    return {
        node,
    };
}

/**
 * RepeatableContainerNode contains RepeatableTitleNode and RepeatableContentNode
 */
export class RepeatableContainerNode extends ElementNode {
    __open: boolean;
    __repeat: boolean = true;
    __repeatBy: RepeatBy = "beneficiary";

    getShow(): boolean {
        const self = this.getLatest();
        return self.__repeat;
    }

    toggleShow(): void {
        this.setShow(!this.getShow());
    }

    setShow(value: boolean) {
        const self = this.getWritable();
        self.__repeat = value;
    }

    constructor(open: boolean, repeat: boolean, repeatBy: RepeatBy = "beneficiary", key?: NodeKey) {
        super(key);
        this.__open = open;
        this.__repeat = repeat;
        this.__repeatBy = repeatBy;
    }

    getRepeatBy(): RepeatBy {
        return this.getLatest().__repeatBy ?? "beneficiary";
    }

    static getType(): string {
        return "repeatable-container";
    }

    static clone(node: RepeatableContainerNode): RepeatableContainerNode {
        return new RepeatableContainerNode(node.__open, node.__repeat, node.__repeatBy, node.__key);
    }

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
        const dom = document.createElement("details");
        dom.open = this.__open;
        dom.classList.add("Repeatable_container");
        if (this.__repeatBy === "article" && !isContractValidationPage()) {
            dom.classList.add("Repeatable_container--article");
        }
        if (isContractValidationPage()) addClassNamesToElement(dom, "contract-validation-page");
        if (!isContractValidationPage()) addClassNamesToElement(dom, config.theme.repeatableContainer);
        return dom;
    }

    updateDOM(prevNode: RepeatableContainerNode, dom: HTMLDetailsElement): boolean {
        if (prevNode.__open !== this.__open) {
            dom.open = this.__open;
        }
        return false;
    }

    static importJSON(serializedNode: SerializedRepeatableContainerNode): RepeatableContainerNode {
        const node = $createRepeatableContainerNode(
            serializedNode.open,
            serializedNode.repeat,
            serializedNode.repeatBy ?? "beneficiary",
        );
        return node;
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput {
        const { element } = super.exportDOM(editor);
        return { element };
    }

    exportJSON(): SerializedRepeatableContainerNode {
        return {
            ...super.exportJSON(),
            open: this.__open,
            repeat: this.__repeat,
            repeatBy: this.__repeatBy,
            type: "repeatable-container",
            version: 1,
        };
    }

    setOpen(open: boolean): void {
        const writable = this.getWritable();
        writable.__open = open;
    }

    getOpen(): boolean {
        return this.getLatest().__open;
    }

    toggleOpen(): void {
        this.setOpen(!this.getOpen());
    }
}

export function $createRepeatableContainerNode(
    isOpen: boolean,
    repeat: boolean,
    repeatBy: RepeatBy = "beneficiary",
): RepeatableContainerNode {
    return new RepeatableContainerNode(isOpen, repeat, repeatBy);
}

export function $isRepeatableContainerNode(node: LexicalNode | null | undefined): node is RepeatableContainerNode {
    return node instanceof RepeatableContainerNode;
}
